import React, { useContext, useState } from 'react';
import { VStack, Heading, Button, Text, InputGroup, InputRightElement, Progress, Box, useToast } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useLocation, useNavigate } from 'react-router-dom';
import TextField from '../../TextField';
import { ViewIcon, ViewOffIcon, WarningIcon } from '@chakra-ui/icons';
import NoUserHeader from '../../AreaLivre/NoUserHeader';
import { api } from 'api/api';
import { AccountContext } from 'components/AccountContext';

import LogoDashboard from '../../../images/petalas-deovita.svg';
const ENDPOINT =
  process.env.REACT_APP_ENVIRONMENT === 'production'
    ? process.env.REACT_APP_BASE_URL_API_SERVER_PROD
    : process.env.REACT_APP_BASE_URL_API_SERVER_TEST;

function RecuperaSenha() {
  const { setUser } = useContext(AccountContext);
  const [show, setShow] = React.useState(false);
  const [error, setError] = React.useState(null);
  const onlyNumbers = (str) => str.replace(/[^0-9]/g, '');
  const handleClick = () => setShow(!show);
  const [rule, setRule] = React.useState(/[0-9a-zA-Z!?$*&@#-]{8,}$/);
  const [visibility, setVisibility] = React.useState('');
  const navigate = useNavigate();
  const { state } = useLocation();
  const [progress, setProgress] = useState(0);
  const [barColor, setBarColor] = useState('red');
  const [strengthText, setStrengthText] = useState('');
  const [password, setPassword] = useState('');
  const passwordStrength = (string) => {
    let strength = 0;

    if (/[A-Z]/.test(string)) {
      strength += 20;
    }

    if (/[a-z]/.test(string)) {
      strength += 20;
    }

    if (/[0-9]/.test(string)) {
      strength += 20;
    }

    if (/[!?$*&@#-]/.test(string)) {
      strength += 20;
    }

    if (/[0-9a-zA-Z!?$*&@#-]{8,}$/.test(string)) {
      strength += 20;
    }
    setProgress(strength);
    if (strength === 100) {
      setBarColor('green');
      setStrengthText('Senha Forte');
    }
    if (strength <= 20 && strength >= 0) {
      setBarColor('red');
      setStrengthText('Senha Fraca');
    }
    if (strength <= 40 && strength > 20) {
      setBarColor('orange');
      setStrengthText('Senha Média');
    }
    if (strength < 100 && strength > 40) {
      setBarColor('blue');
      setStrengthText('Senha Razoavel');
    }
  };
  const toast = useToast();
  async function loginUser(credentials) {
    try {
      const res = await api(`${ENDPOINT}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: onlyNumbers(credentials.username), password: credentials.password }),
      });

      if (!res || !res.ok || res.status >= 400) {
        setError('Não foi possível realizar login no momento. Tente novamente mais tarde.');
        return;
      }

      const data = await res.json();

      if (!data || data.loggedIn === false) {
        setError('CPF ou senha incorretos. Tente novamente.');
        return;
      }

      if (data?.token) {
        localStorage.setItem('token', data.token);
      }

      const postMessageReactNative = {
        event: 'login',
        data: data,
      };
      
      window?.ReactNativeWebView?.postMessage(JSON.stringify(postMessageReactNative));
      
      setUser((prevState) => ({
        ...prevState,
        ...data,
        device: prevState.device,
        coords: prevState.coords ? prevState.coords : null,
        build: prevState.build ? prevState.build : null,
      }));

      if (data.status) {
        setError(data.status);
      } else if (data.loggedIn) {
        toast({
          duration: 4000,
          isClosable: true,
          position: 'bottom',
          render: () => (
            <Box
              display="flex"
              alignItems="center"
              bgGradient="linear(135deg, #5AB2AE 0%, #4AA09C 100%)"
              color="white"
              borderRadius="xl"
              boxShadow="lg"
              px={6}
              py={4}
              maxW="350px"
              mx="auto"
            >
              <Box
                minW="48px"
                minH="48px"
                maxW="48px"
                maxH="48px"
                mr={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
                boxShadow="md"
                border="2px solid #5AB2AE"
                overflow="hidden"
              >
                <img src={LogoDashboard} alt="Logo DeoVita" style={{ width: '40px', height: '40px', objectFit: 'contain', display: 'block' }} />
              </Box>
              <Box>
                <Text fontWeight="bold" fontSize="lg" mb={1} color="#fff">
                  Sucesso
                </Text>
                <Text fontSize="sm" color="#fff">
                  Sua senha foi alterada com sucesso! 
                </Text>
              </Box>
            </Box>
          ),
        });
        
        if (data.id_contrato === 0 && data.titular === true) {
          if (data?.clienteImportado?.clientImportado === true) {
            navigate('/dashboard');
            return;
          }
          if (data.contrato_pendente_pagamento === true) {
            navigate('/cadastro/analise', {
              state: {
                client_id: data.id_cliente_deovita,
                name: data.name,
                cpf: data.cpf,
              },
            });
          } else {
            navigate('/dashboard');
          }
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      setError('Erro no login após recuperação de senha');
    }
  }

  return (
    <>
      <NoUserHeader />
      <Formik
        initialValues={{ senha: '', senha2: '' }}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={Yup.object({
          senha: Yup.string().trim().required('Senha Obrigatória!').matches(rule, 'Senha fora de padrão'),
          senha2: Yup.string()
            .required('Confirmação da senha é obrigatória')
            .test('pass-match', 'Senhas precisam ser iguais', function (value) {
              return this.parent.senha === value;
            }),
        })}
        onSubmit={async (values, actions) => {
          try {
            const vals = {
              password: values.senha,
              username: onlyNumbers(state.cpf),
              hash: state.hash,
            };
            
            const res = await api(`${ENDPOINT}/auth/update_password`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(vals),
            });

            if (!res || !res.ok || res.status >= 400) {
              setError('Erro na Rede');
              return;
            }

            if (res.status === 200) {
              setError('');
            }

            const data = await res.json();

            if (!data) return;

            if (data.success === false) {
              setError('Falha na atualização da senha');
              return;
            }

            if (data.success === true) {
              setError('');
              await loginUser({ username: state.cpf, password: values.senha });
            }
          } catch (err) {
            setError('Erro na atualização da senha');
          }
        }}
      >
        {({ values, errors }) => (
          <VStack as={Form} w={{ base: '90%', md: '500px' }} m="auto" justify="top" marginTop="5%" marginBottom="5%" spacing="1rem" align="start">
            <Heading fontSize="1em">Escolha uma nova senha de no mínimo 8 caracteres.</Heading>
            <InputGroup size="md">
              <TextField
                pr="4.5rem"
                name="senha"
                id="senha"
                placeholder="Senha"
                type={show ? 'text' : 'password'}
                onKeyUp={(e) => {
                  e.preventDefault();
                  const filteredValue = e.target.value.replace(/[^0-9a-zA-Z!?$*&@#-]/g, '');
                  e.target.value = filteredValue;
                  values.senha = filteredValue;
                  passwordStrength(filteredValue);
                }}
              />
              <InputRightElement width="4rem" pt="1rem" size="md">
                <Button h="1.75rem" size="sm" onClick={handleClick} leftIcon={show ? <ViewIcon /> : <ViewOffIcon />}></Button>
              </InputRightElement>
            </InputGroup>
            <InputGroup size="md" id="senha2" pb="15%">
              <TextField pr="4.5rem" name="senha2" placeholder="Repita a senha" type={show ? 'text' : 'password'} />
              <InputRightElement width="4rem" pt="1rem" size="md">
                <Button h="1.75rem" size="sm" onClick={handleClick} leftIcon={show ? <ViewIcon /> : <ViewOffIcon />}></Button>
              </InputRightElement>
            </InputGroup>
            <Progress w="100%" value={progress} colorScheme={barColor} borderRadius="15px" />
            <Text color={barColor}>{strengthText ? strengthText + '!' : ''}</Text>
            <Button colorScheme="teal" w="100%" type="submit">
              Avançar
            </Button>
          </VStack>
        )}
      </Formik>
    </>
  );
}

export default RecuperaSenha;
